import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CapabilityShowcase = () => {
  const capabilities = [
    {
      id: 1,
      icon: 'Code2',
      title: 'SaaS Development',
      description: 'End-to-end SaaS application development with scalable architecture, modern tech stacks, and cloud-native deployment strategies.',
      features: ['Microservices Architecture', 'API Development', 'Cloud Integration', 'Performance Optimization'],
      color: 'var(--color-primary)'
    },
    {
      id: 2,
      icon: 'Rocket',
      title: 'MVP Development',
      description: 'Rapid MVP development for startups with lean methodology, iterative approach, and market validation focus.',
      features: ['Rapid Prototyping', 'Market Testing', 'User Feedback', 'Iterative Development'],
      color: 'var(--color-accent)'
    },
    {
      id: 3,
      icon: 'Layers',
      title: 'Product Scaling',
      description: 'Scale your existing product with performance optimization, infrastructure enhancement, and feature expansion.',
      features: ['Load Balancing', 'Database Optimization', 'Caching Strategies', 'Auto-scaling'],
      color: 'var(--color-conversion-accent)'
    },
    {
      id: 4,
      icon: 'Shield',
      title: 'Security & Compliance',
      description: 'Enterprise-grade security implementation with compliance standards, data protection, and vulnerability management.',
      features: ['SOC 2 Compliance', 'GDPR Ready', 'Penetration Testing', 'Security Audits'],
      color: 'var(--color-warning)'
    },
    {
      id: 5,
      icon: 'Smartphone',
      title: 'Mobile Development',
      description: 'Native and cross-platform mobile applications with seamless user experience and offline capabilities.',
      features: ['React Native', 'iOS & Android', 'Offline Support', 'Push Notifications'],
      color: 'var(--color-conversion-accent)'
    },
    {
      id: 6,
      icon: 'Zap',
      title: 'DevOps & CI/CD',
      description: 'Automated deployment pipelines, infrastructure as code, and continuous integration for faster releases.',
      features: ['Docker & Kubernetes', 'AWS/Azure/GCP', 'Automated Testing', 'Monitoring & Alerts'],
      color: 'var(--color-conversion-accent)'
    }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16 lg:mb-20">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-xs md:text-sm font-medium font-body mb-4 md:mb-6">
            <Icon name="Sparkles" size={16} />
            <span>Our Capabilities</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-foreground mb-4 md:mb-6">
            Technical Excellence Meets Business Strategy
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-muted-foreground font-body max-w-3xl mx-auto">
            We combine deep technical expertise with strategic thinking to deliver solutions that drive measurable business outcomes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {capabilities?.map((capability) => (
            <div
              key={capability?.id}
              className="group relative bg-card border border-border rounded-xl p-6 md:p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary/5 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
              <div className="relative">
                <div
                  className="w-12 h-12 md:w-14 md:h-14 rounded-lg flex items-center justify-center mb-4 md:mb-6 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${capability?.color}15` }}
                >
                  <Icon name={capability?.icon} size={24} color={capability?.color} />
                </div>

                <h3 className="text-xl md:text-2xl font-bold font-headline text-foreground mb-3 md:mb-4">
                  {capability?.title}
                </h3>

                <p className="text-sm md:text-base text-muted-foreground font-body mb-4 md:mb-6 line-clamp-3">
                  {capability?.description}
                </p>

                <ul className="space-y-2 md:space-y-3 mb-4 md:mb-6">
                  {capability?.features?.map((feature, index) => (
                    <li key={index} className="flex items-center space-x-2 text-xs md:text-sm text-muted-foreground font-body">
                      <Icon name="Check" size={16} color={capability?.color} />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/services"
                  className="inline-flex items-center space-x-2 text-sm md:text-base font-medium font-body text-primary hover:text-primary/80 transition-colors duration-200"
                >
                  <span>Learn More</span>
                  <Icon name="ArrowRight" size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12 md:mt-16">
          <Button
            variant="outline"
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={() => window.location.href = '/services'}
          >
            View All Services
          </Button>
        </div>
      </div>
    </section>
  );
};

export default CapabilityShowcase;